<template>
  <div class="f-ast f-v">
    <donut-modal-sections-title :title="`Configure ${capitalize($i18n.t('shoutouts.reward', { count: 1 }))}`" />
    <div class="flex">
      <div class="f-v f-gap-8 w-min-500 w-max-500 p-14">
        <div class="f-h">
          <donut-form-input
            class="m-bx2 fc-f2"
            :for="storeListing.name"
            name="reward_name"
            :validator="presenceValidator('Name')"
          >
            <input
              v-model="storeListing.name"
              type="text"
              :placeholder="listingDefaults.name"
              maxLength="40"
            >
          </donut-form-input>
          <!-- Badge / auto-redeem presets (e.g. Donut Lore): no custom image, points, or quantity -->
          <donut-form-input v-if="!presetTemplateLocked" class="m-lx2 flexed" name="image">
            <donut-upload
              button-icon="image"
              button-text="Upload Image"
              button-style-class="w-100p inv"
              :records="storeListing.image"
              record-update-mutation="rewardsListings/updateListingBeingEditedImage"
              :allow-multiple="false"
              :permitted-file-types="['image/*']"
              acl="private"
              :size-limit="50000000"
            />
          </donut-form-input>
        </div>
        <donut-form-input
          class="m-bx2"
          :for="storeListing.short_description"
          name="reward_short_description"
          :validator="presenceValidator('Short description')"
        >
          <input
            v-model="storeListing.short_description"
            type="text"
            :placeholder="listingDefaults.short_description"
            maxLength="80"
          >
        </donut-form-input>
        <donut-form-input
          :for="'description'"
          name="reward_description"
        >
          <donut-input-text
            v-model="storeListing.description"
            placeholder="Further details (optional and available on hover)"
            :max-length="250"
            :autocomplete="['emoji']"
            :textarea="true"
            :autogrow="true"
            :wysiwyg-ignore="['link']"
          />
        </donut-form-input>
        <div
          v-if="storeListing.is_gift_card"
          class="m-bx2"
        >
          <div class="t-xl m-bx2">How does gift card redemption work?</div>
          <div v-emoji="giftCardDescriptionCopy" class="t" style="width: max-content; line-height: 2; white-space: pre-line;" />
          <div v-if="giftCardStatus && giftCardStatus !== 'eligible'" class="t warning-yellow m-t5">{{ giftCardStatusCopy[giftCardStatus].description }}</div>
        </div>
        <template v-else>
          <shoutouts-rewards-listing-points-cost-input
            v-if="!presetTemplateLocked"
            class="m-bx2"
            :for="storeListing.points_cost"
            :is-monetary="storeListing.is_monetary"
            :dollars-per-point="dollarsPerPoint"
            :points-config="pointsConfig"
            name="reward_points"
            :validators="pointsCostValidators"
          />
          <div v-if="!presetTemplateLocked" class="f-h m-bx2">
            <donut-form-input
              :for="hasQuantity"
              name="reward_has_quantity"
            >
              <div class="f-h">
                <donut-input-toggle
                  v-model="hasQuantity"
                  name="reward_has_quantity"
                  :label="`Limited quantity`"
                  update-mutation="rewardsListings/updateListingBeingEdited"
                  :update-args="{ key: 'quantity_available', value: !hasQuantity ? 0 : null }"
                />
                <i
                  v-tooltip="rewardQuantityTooltip"
                  class="mi md-18 gray p-8 m-rx4"
                >info</i>
              </div>
            </donut-form-input>
            <donut-form-input
              v-if="hasQuantity"
              :for="storeListing.quantity_available"
              name="reward_quantity"
            >
              <div class="f-h pos-abs">
                <div>
                  <input
                    v-model="quantityAvailable"
                    class="w-max-70"
                    type="number"
                    min="0"
                    placeholder="-"
                  >
                  <label
                    class="t gray"
                    for="reward_quantity"
                  >
                    Quantity available to be redeemed
                  </label>
                </div>
              </div>
            </donut-form-input>
          </div>
        </template>
        <donut-form-input
          :for="'state'"
          name="reward_state"
        >
          <div class="f-h">
            <donut-input-toggle
              v-model="isActive"
              class="m-bx2"
              name="reward_state"
              label="Visible in store"
              update-mutation="rewardsListings/updateListingBeingEdited"
              :update-args="{ key: 'state', value: isActive ? 'inactive' : 'active' }"
            />
            <i
              v-tooltip="rewardActiveTooltip"
              class="mi md-18 gray p-8"
            >info</i>
          </div>
        </donut-form-input>
      </div>
      <div class="f-v">
        <shoutouts-rewards-store-listing
          class="w-min-288 w-max-288"
          :listing="listingForPreview"
          :points-config="pointsConfig"
          :preview="true"
        />
      </div>
    </div>
    <donut-modal-sections-actions
      v-if="storeListing.is_gift_card && giftCardStatus && giftCardStatus !== 'eligible'"
      :button-text="giftCardStatusCopy[giftCardStatus].buttonText"
      save-action="rewardsListings/handleGiftCardErrorModal"
      :save-args="{ pointsConfig, error: giftCardStatus }"
      :is-save-disabled="errors && compact(Object.values(errors)).length > 0"
    />
    <donut-modal-sections-actions
      v-else
      button-text="Save"
      save-action="rewardsListings/saveOrUpdateListing"
      :save-args="saveArgs"
      :is-save-disabled="errors && compact(Object.values(errors)).length > 0"
    />
  </div>
</template>

<script setup>
  import { computed, onBeforeMount, inject } from 'vue';
  import { useStore } from 'vuex';
  import { capitalize, compact } from 'lodash';
  import useValidators from 'composables/validators';

  const $i18n = inject('$i18n');

  const { presenceValidator, integerRangeValidator } = useValidators();

  const { state, dispatch, commit } = useStore();
  const errors = computed(() => state.errors);
  const listing = computed(() => state.modal.data.listing);
  const preset = computed(() => state.modal.data.preset);
  const pointsConfig = computed(() => state.modal.data.pointsConfig);
  const dollarsPerPoint = computed(() => state.modal.data.dollarsPerPoint);
  const storeListing = computed(() => state.rewardsListings.listingBeingEdited);
  const giftCardStatus = computed(() => state.rewardsListings.giftCardStatus);

  const presetTemplateLocked = computed(() => Boolean(storeListing.value.badge || storeListing.value.autoredeemable));

  const isActive = computed({
    get: () => storeListing.value.state === 'active',
    set: (value) => {
      commit('rewardsListings/updateListingBeingEdited', {
        key: 'state',
        value: value ? 'active' : 'inactive',
      });
    },
  });
  const quantityAvailable = computed({
    get: () => storeListing.value.quantity_available,
    set: (value) => {
      commit('rewardsListings/updateListingBeingEdited', {
        key: 'quantity_available',
        value,
      });
    },
  });
  const hasQuantity = computed(() => {
    const quantity = storeListing.value.quantity_available;
    return quantity !== null && quantity !== undefined;
  });

  const pointsCostValidators = [
    presenceValidator(`a cost of at least 1 ${$i18n.t('shoutouts.point', { count: 1 })}`),
    integerRangeValidator(1),
  ];

  const giftCardDescriptionCopy = computed(() => `Gift cards are redeemed at a rate of ${Math.round(dollarsPerPoint.value * 100)}¢ per :${pointsConfig.value.emoji}:
    Redeemers can choose amounts between $5 and $100 to use at over 100 stores worldwide.
    Your credit card on file will be charged on the 1st of each month for the total value redeemed plus an 8% service fee.`);

  const rewardPointsTooltip = `The number of ${$i18n.t('shoutouts.point', { count: 2 })} required to redeem the reward.`;
  const rewardQuantityTooltip = `Useful if you have a set inventory of swag, or for ${$i18n.t('shoutouts.reward', { count: 2 })} that otherwise make sense to limit in quantity.`;
  const rewardActiveTooltip = `Turn this off to hide the ${$i18n.t('shoutouts.reward', { count: 1 })} from employees.`;
  const listingDefaults = {
    name: `${capitalize($i18n.t('shoutouts.reward', { count: 1 }))} Name`,
    short_description: `Short description of ${$i18n.t('shoutouts.reward', { count: 1 })}.`,
    description: null,
    points_cost: null,
    quantity_available: null,
    gift_card: false,
    is_monetary: true,
    state: 'active',
  };

  // This is not reachable currently, because gift card listings can only be edited
  // in this modal while active, in which case we're not loading giftCardStatus
  const giftCardStatusCopy = {
    upgrade_required: {
      // Direct to /billing
      description: 'Please upgrade to a paid plan to use gift cards.',
      buttonText: 'View Plans',
    },
    finish_trial: {
      // open intercom chat
      description: 'Gift cards are not available on a free trial. For questions please contact support.',
      buttonText: 'Contact Support',
    },
    blocked: {
      // open intercom chat
      description: 'Gift cards are not available. For questions please contact support.',
      buttonText: 'Contact Support',
    },
    invoiced: {
      buttonText: 'Get Started',
    },
  };

  onBeforeMount(() => {
    if (listing.value.id) {
      dispatch('rewardsListings/initializeListingEdit', {
        ...listing.value,
      });
    }
  });

  const saveArgs = computed(() => {
    const listingToSave = { ...storeListing.value };
    if (listing.value?.id && preset.value) {
      listingToSave.preset_id = listing.value.id;
      delete listingToSave.id;
    }
    if (!listingToSave.state) {
      listingToSave.state = 'inactive';
    }

    return listingToSave;
  });

  const listingForPreview = computed(() => {
    const resultListing = { ...state.rewardsListings.listingBeingEdited };
    Object.entries(listingDefaults).forEach(([key, defaultValue]) => {
      if (resultListing[key] === null || resultListing[key] === '') resultListing[key] = defaultValue;
    });
    return resultListing;
  });
</script>
